import { dateToMinuts, minutesToTimeFormat, minutsToHoursShort } from "@/utils/dateUtils";
import { format, formatDistance, isSameYear } from "date-fns";
import { enGB, da, sv, nb } from "date-fns/locale";
const locales = { en: enGB, da, sv, no: nb };
import i18n from "../../i18n";
import { minutesFromHoursMinutesDecimal, numberToCommaStringFormat } from "../../utils/numberUtils";

const filters = {
  orderby(list, key) {
    list.sort((a, b) => {
      const x: any = a[key];
      const y: any = b[key];
      return x < y ? -1 : x > y ? 1 : 0;
    });
  },

  minutsToHoursShort(totalMinutes): string {
    return minutsToHoursShort(totalMinutes);
  },

  minutsToHours(totalMinutes, sumMultiple = false) {
    return minutesToTimeFormat(totalMinutes, sumMultiple);
  },

  padTo2Digits(num) {
    return num.toString().padStart(2, "0");
  },

  timedistance(timeValue: Date): string {
    if (!timeValue) {
      return "";
    }
    return formatDistance(timeValue, new Date(), {
      addSuffix: true,
      locale: locales[i18n.global.locale]
    });
  },

  hourDecimalToHourColonMinutes(hourDecimal: number) {
    const minutes = minutesFromHoursMinutesDecimal(hourDecimal);
    const hours = Math.floor(hourDecimal);
    const hoursX = Math.floor(minutes / 60);
    const hoursTotal = hours + hoursX;
    const minutesTotal = minutes - hoursX * 60;
    //'0' + slice(-2) padder tal med 0 hvis de er mellem 0-9.
    return ("0" + hoursTotal).slice(-2) + ":" + ("0" + minutesTotal).slice(-2);
  },

  date(timestampValue, formatString = "yyyy-MM-dd") {
    if (!timestampValue) {
      return "";
    }
    return format(timestampValue, formatString);
  },

  dateAndYear(timestampValue, formatString = "d.M.yyyy") {
    if (!timestampValue) {
      return "";
    }
    return format(timestampValue, formatString);
  },

  dateAndYearShort(timestampValue, formatString = "d.M.yy") {
    if (!timestampValue) {
      return "";
    }
    return format(timestampValue, formatString);
  },

  time(timestampValue, formatString = "HH:mm") {
    if (!timestampValue) {
      return "";
    }
    return format(timestampValue, formatString);
  },

  dateTime(timestampValue, formatString = "d.M.yyyy HH:mm") {
    if (!timestampValue) {
      return "";
    }
    return format(timestampValue, formatString);
  },

  dateTimeDownloadTime(timestampValue, formatString = "yyyy_MM_dd_HHmm") {
    if (!timestampValue) {
      return "";
    }
    return format(timestampValue, formatString);
  },

  dateTimeWithDayName(timestampValue) {
    //"EEE d. MMM kl HH:mm"
    if (!timestampValue) {
      return "";
    }
    const dayAndMonth = format(timestampValue, "EEEE d. MMM", {
      locale: locales[i18n.global.locale]
    });
    return dayAndMonth + " kl " + format(timestampValue, "HH:mm");
  },

  formatDateTime(timestampValue, formatString: string) {
    if (!timestampValue) {
      return "";
    }
    return format(timestampValue, formatString);
  },

  dateShort(timestampValue, formatLongString = "d.M.yy", formatString = "d.M") {
    if (!timestampValue) {
      return "";
    }
    let returnstr: string;
    if (isSameYear(new Date(), timestampValue)) {
      returnstr = format(timestampValue, formatString);
    } else {
      returnstr = format(timestampValue, formatLongString);
    }

    return returnstr;
  },
  dateShortNormPeriod(timestampValue, formatString = "d.M") {
    if (!timestampValue) {
      return "";
    }
    return format(timestampValue, formatString);
  },

  monthShort(timestampValue, formatLongString = "MMM-yy", formatString = "MMM") {
    if (!timestampValue) {
      return "";
    }
    let returnstr: string;
    if (isSameYear(new Date(), timestampValue)) {
      returnstr = format(timestampValue, formatString, {
        locale: locales[i18n.global.locale]
      });
    } else {
      returnstr = format(timestampValue, formatLongString, {
        locale: locales[i18n.global.locale]
      });
    }

    return returnstr;
  },
  monthLong(timestampValue, formatLongString = "MMMM-yyyy", formatString = "MMMM") {
    if (!timestampValue) {
      return "";
    }
    let returnstr: string;
    if (isSameYear(new Date(), timestampValue)) {
      returnstr = format(timestampValue, formatString, {
        locale: locales[i18n.global.locale]
      });
    } else {
      returnstr = format(timestampValue, formatLongString, {
        locale: locales[i18n.global.locale]
      });
    }

    return returnstr;
  },
  dateShortWithDayName(
    timestampValue,
    formatLongString = "EEE  d.M.yy",
    formatString = "EEE  d.M"
  ) {
    if (!timestampValue) {
      return "";
    }
    let returnstr: string;
    if (isSameYear(new Date(), timestampValue)) {
      returnstr = format(timestampValue, formatString, {
        locale: locales[i18n.global.locale]
      });
    } else {
      returnstr = format(timestampValue, formatLongString, {
        locale: locales[i18n.global.locale]
      });
    }

    return returnstr;
  },
  dayName(timestampValue) {
    if (!timestampValue) {
      return "";
    }
    return format(timestampValue, "EEEE", {
      locale: locales[i18n.global.locale]
    });
  },

  numberWithDayName(timestampValue) {
    return format(timestampValue, "dd. EEE", {
      locale: locales[i18n.global.locale]
    });
  },

  numberWithDayNameShort(timestampValue) {
    return format(timestampValue, "dd EEEEEE", {
      locale: locales[i18n.global.locale]
    });
  },

  dateWithDayName(timestampValue) {
    return format(timestampValue, "dd/M EEE", {
      locale: locales[i18n.global.locale]
    });
  },

  dateWithDayNameShort(timestampValue) {
    return format(timestampValue, "dd/M EEEEEE", {
      locale: locales[i18n.global.locale]
    });
  },

  dateAndFromAndToTime(timestampValue1, timestampValue2) {
    const date = format(timestampValue1, "dd/M EEEEEE", {
      locale: locales[i18n.global.locale]
    });
    const from = dateToMinuts(timestampValue1);
    const to = dateToMinuts(timestampValue2);
    return date + "<br>" + this.minutsToHoursShort(from) + "-" + this.minutsToHoursShort(to);
  },

  monthDateCommaYear(timestampValue: Date): string {
    return format(timestampValue, "LLLL d, yyyy", {
      locale: locales[i18n.global.locale]
    });
  },

  monthYear(timestampValue: Date): string {
    return this.capitalizeFirstLetter(
      format(timestampValue, "LLL yy", {
        locale: locales[i18n.global.locale]
      })
    );
  },

  capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  },

  round(value) {
    if (!value) {
      value = 0;
    }

    value = i18n.global.n(+parseFloat(value).toFixed(2), "decimal");

    return value;
  },

  lowercase(value) {
    return value ? value.toLowerCase() : "";
  },

  titlecase(value) {
    return value ? value.charAt(0) + value.slice(1).toLowerCase() : "";
  },

  true_false_to_yes_no(value) {
    return value == true ? i18n.global.t("yes") : i18n.global.t("no");
  },

  numberToCommaStringFormat(value) {
    return numberToCommaStringFormat(value, 2);
  }
};

export default filters;
